/*
  rs-consult.js — keep exactly ONE free nutrition consult in the cart while a
  subscription line is present, and remove it when none remain.

  WHY network interception (not a form/theme event):
  Add-to-cart on this store is app-owned (Rebuy); the theme's ATC events don't
  fire on add. So instead of hooking the pouch-options/PDP form, we watch the
  /cart/(add|change|update) network calls (fetch AND XHR) and, after any cart
  mutation, reconcile the cart to a single invariant:

      consult present  <=>  a subscription line is present   (qty always 1)

  This satisfies all requirements from any entry point:
    - Subscription added on the PDP  -> consult is added (once).
    - No subscription lines remain    -> consult is removed.
    - Never more than one consult (presence is checked before adding).

  A line counts as a subscription when it has selling_plan_allocation /
  selling_plan (native Shopify selling plans — exactly what pouch-options sets).

  Config: window.rsConsultVariantId (theme setting `consult_product`).
          Unset  -> this whole script is a no-op (safe to ship disabled).
  Debug:  window.rsConsultDebug = true  -> logs every decision.
*/
(function () {
  var VARIANT_ID = window.rsConsultVariantId || null;
  if (!VARIANT_ID) return; // not configured -> no-op

  var VID = String(VARIANT_ID);

  var routes = window.routes || {};
  var CART_URL = routes.cart_url || '/cart';
  var ADD_URL = routes.cart_add_url || '/cart/add.js';
  var CHANGE_URL = routes.cart_change_url || '/cart/change.js';

  // Native fetch, captured BEFORE we wrap window.fetch below, so all of our own
  // cart reads/writes bypass the interceptor and can never trigger a reconcile.
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!nativeFetch) return; // no fetch -> can't operate; bail safely

  var dbg = function () {
    if (window.rsConsultDebug) console.log.apply(console, ['[consult]'].concat([].slice.call(arguments)));
  };

  var busy = false; // single-flight: only one reconcile at a time
  var queued = false; // a mutation arrived mid-reconcile -> run once more after
  var giveUp = false; // an add failed this pageload (e.g. sold out) -> stop retrying
  var timer = null;

  // ---- cart reads ----
  var getCart = function () {
    return nativeFetch(CART_URL + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  };
  var isSubLine = function (l) {
    return !!(l && (l.selling_plan_allocation || l.selling_plan));
  };
  var subCount = function (cart) {
    return (cart.items || []).filter(isSubLine).length;
  };
  var findConsult = function (cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === VID) return items[i];
    }
    return null;
  };

  // ---- cart-drawer re-render (theme's rs-cart sections) ----
  var sectionIds = function () {
    var ids = [];
    document.querySelectorAll('[data-rs-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-cart-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  };
  var fetchSection = function (id) {
    return nativeFetch(CART_URL + '?sections=' + encodeURIComponent(id), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) { return j && j[id] != null ? j[id] : null; });
  };
  var renderOne = function (sectionEl, id, cartJson) {
    var inResp = cartJson && cartJson.sections && cartJson.sections[id];
    var htmlPromise = inResp != null ? Promise.resolve(inResp) : fetchSection(id);
    return htmlPromise.then(function (html) {
      if (html == null) return;
      var slide = sectionEl.querySelector('.js-cart-slide');
      var wasActive = !!(slide && slide.classList.contains('is-active'));

      // Preserve already-populated Rebuy cross-sell widgets across the innerHTML
      // swap — the fresh section HTML only has EMPTY [data-rebuy-id] placeholders
      // and Rebuy won't re-fill them after a programmatic re-render.
      var savedRebuy = {};
      sectionEl.querySelectorAll('[data-rebuy-id]').forEach(function (el) {
        if (el.children.length || el.innerHTML.trim() !== '') savedRebuy[el.getAttribute('data-rebuy-id')] = el;
      });

      sectionEl.innerHTML = html;

      sectionEl.querySelectorAll('[data-rebuy-id]').forEach(function (placeholder) {
        var saved = savedRebuy[placeholder.getAttribute('data-rebuy-id')];
        if (saved) placeholder.replaceWith(saved);
      });

      // Only restore the open state if the drawer was ALREADY open — we never
      // force the drawer open just because we reconciled the consult.
      if (wasActive) {
        var newSlide = sectionEl.querySelector('.js-cart-slide');
        if (newSlide) {
          newSlide.classList.add('is-active');
          newSlide.setAttribute('aria-hidden', 'false');
        }
        document.body.classList.add('has-open-cart-drawer');
        var trigger = document.querySelector('.js-cart-drawer-trigger');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }

      if (typeof window.initSelects === 'function') window.initSelects(sectionEl);
    });
  };
  var rerender = function (cartJson) {
    var seen = [];
    var chain = Promise.resolve();
    document.querySelectorAll('[data-rs-cart-section]').forEach(function (wrap) {
      var sectionEl = wrap.closest('.shopify-section') || wrap;
      var id = wrap.getAttribute('data-cart-section-id');
      if (!id || seen.indexOf(sectionEl) !== -1) return;
      seen.push(sectionEl);
      chain = chain.then(function () { return renderOne(sectionEl, id, cartJson); });
    });
    return chain.then(function () {
      document.dispatchEvent(new CustomEvent('rs:cart:updated', { detail: { source: 'consult' } }));
    });
  };

  // ---- cart writes (via nativeFetch, so they never re-trigger us) ----
  var cartWrite = function (url, payload) {
    var ids = sectionIds();
    if (ids.length) payload.sections = ids.join(',');
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (json && json.status) return json; // error payload -> skip re-render
        return rerender(json).then(function () { return json; });
      });
  };
  var addConsult = function () {
    if (giveUp) return Promise.resolve();
    dbg('adding consult', VID);
    return cartWrite(ADD_URL, { id: VARIANT_ID, quantity: 1 }).then(function (json) {
      if (json && json.status) {
        giveUp = true; // e.g. sold out / unavailable — don't hammer the cart
        dbg('add failed, disabling for this pageload', json);
      }
      return json;
    });
  };
  var removeConsult = function (line) {
    dbg('removing consult', line.key);
    return cartWrite(CHANGE_URL, { id: line.key, quantity: 0 });
  };

  // ---- reconcile cart to the invariant ----
  var reconcile = function () {
    if (busy) { queued = true; return; }
    busy = true;
    getCart()
      .then(function (cart) {
        var subs = subCount(cart);
        var consult = findConsult(cart);
        dbg('reconcile: subs=' + subs + ' consult=' + (consult ? 'yes' : 'no'));
        if (subs > 0 && !consult) return addConsult();
        if (subs === 0 && consult) return removeConsult(consult);
        return null; // invariant already holds -> nothing to do
      })
      .catch(function (e) { dbg('reconcile error', e); })
      .then(function () {
        busy = false;
        if (queued) { queued = false; schedule(); }
      });
  };
  var schedule = function () {
    if (timer) clearTimeout(timer);
    timer = setTimeout(reconcile, 250); // debounce bursts of cart calls into one pass
  };

  // ---- detect cart mutations made by the app / theme ----
  var isCartWrite = function (url) {
    if (!url) return false;
    return /\/cart\/(add|change|update|clear)(\.js)?($|\?)/.test(String(url));
  };

  // fetch
  if (window.fetch) {
    var wrappedFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var method = (init && init.method) || (input && input.method) || 'GET';
      var watch = String(method).toUpperCase() === 'POST' && isCartWrite(url);
      var result = wrappedFetch.apply(this, arguments);
      if (watch && result && typeof result.then === 'function') {
        result.then(function () { schedule(); }).catch(function () {});
      }
      return result;
    };
  }

  // XHR
  if (window.XMLHttpRequest) {
    var proto = window.XMLHttpRequest.prototype;
    var origOpen = proto.open;
    var origSend = proto.send;
    proto.open = function (method, url) {
      this.__rsConsultWatch = String(method || '').toUpperCase() === 'POST' && isCartWrite(url);
      return origOpen.apply(this, arguments);
    };
    proto.send = function () {
      if (this.__rsConsultWatch) {
        this.addEventListener('load', function () { schedule(); });
      }
      return origSend.apply(this, arguments);
    };
  }

  // ---- initial pass: enforce the invariant for carts from a prior visit ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  dbg('loaded — consult variant', VID);
})();
